import {
  type CSSProperties,
  type KeyboardEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { chainStore } from "@/stores/chain/store";
import ChainCardMeta from "@/components/shopwindow/ChainCardMeta";
import * as analytics from "@/modules/analytics";
import chainImpressions from "@/modules/chainImpressions";
import chainPrompt from "@/modules/chainPrompt";
import { cleanChainTitle } from "@/modules/chainTitle";
import { chainThumbUrl } from "@/modules/chainThumb";
import { isChainRevealed } from "@/modules/chainReveal";
import { isPixieOpen } from "@/modules/pixieWorkspace";
import { CHAIN_COUNTDOWN_SECONDS } from "@/stores/chain/config";
import { eventNames, valueNames } from "@/modules/Constants";
import { useOverlayHeightVar } from "@/components/shopwindow/useOverlayHeightVar";
import {
  type BlockState,
  type NextVideoBlockProps,
  type ResolvedProps,
  experienceIdOf,
  visiblePick,
} from "@/components/shopwindow/nextVideoBlockModel";

const RING_RADIUS = 19; // matches the 44px badge/ring
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS; // ~119.38

type WebkitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
};

type StateUpdate =
  | Partial<BlockState>
  | ((state: BlockState) => Partial<BlockState>);

/**
 * The "next video" prompt: the top recommendation plus Play Now. The overlay
 * instance appears on the last method step; the inline instance appears at the
 * chain reveal, runs the end-of-video countdown and auto-advances unless
 * cancelled. The dismissed flag lives in the chainPrompt singleton so it
 * survives tab remounts.
 */
const BLOCK =
  "tw:flex tw:flex-none tw:flex-col tw:gap-[10px] tw:bg-[#fff] tw:[padding:12px_14px] tw:text-[#c43e3e] tw:[border-top:2px_solid_currentColor]";
const OVERLAY =
  "tw:absolute tw:right-0 tw:bottom-0 tw:left-0 tw:z-5 tw:[box-shadow:0_-6px_18px_rgba(0,0,0,0.14)] tw:[.landscape_&]:right-[65px] tw:[.landscape_&]:max-h-[55%] tw:[.landscape_&]:overflow-y-auto";
const THUMB =
  "tw:relative tw:flex tw:aspect-[16/10] tw:w-[84px] tw:flex-none tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-[4px] tw:[background:linear-gradient(135deg,#c9772f,#8a4a1e)_center/cover]";
const ACTION =
  "tw:flex-1 tw:cursor-pointer tw:rounded-[8px] tw:[font-family:inherit] tw:text-[13px] tw:font-[600]";

export default function NextVideoBlock({
  variant = "inline",
  ...rest
}: NextVideoBlockProps) {
  const props: ResolvedProps = { variant, ...rest };
  const { compact } = props;

  const [state, setStateRaw] = useState<BlockState>(() => ({
    chain: chainStore.getChain(),
    status: chainStore.getStatus(),
    dismissed: false,
    secondsLeft: null,
    zeroCount: 0,
  }));

  // Props and state of the latest commit: timers and callbacks read these, as
  // the class read this.props and this.state.
  const latest = useRef({ props, state });
  const forId = useRef<unknown>(null); // experience id the block state belongs to
  const advanced = useRef(false); // fired the auto-advance for this experience yet?
  const countdownFsSuppress = useRef(false); // fullscreen suppressed the advance when the countdown began
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Merges like a class setState: always a new object, so it always re-renders.
  const setState = (update: StateUpdate) => {
    setStateRaw(prev => ({
      ...prev,
      ...(typeof update === "function" ? update(prev) : update),
    }));
  };

  const experienceId = () => experienceIdOf(latest.current.props);

  const clearCountdown = () => {
    if (countdownTimer.current != null) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
  };

  const playNow = (source: string) => {
    const pick = (latest.current.state.chain || [])[0];
    if (!pick) {
      return;
    }
    clearCountdown();
    // 'auto' = the countdown reached zero; 'play-now' = the button.
    analytics.track(
      source === "auto" ? "Chain Auto Advance" : "Chain Play Now",
      {
        pix_from_idexperience: experienceId(),
        pix_to_idexperience: pick.experience_id,
      },
    );
    if (latest.current.props.onApplicationEvent) {
      latest.current.props.onApplicationEvent({
        name: eventNames.CHAIN_ADVANCE,
        idexperience: pick.experience_id,
        region: "Next Video Block",
      });
    }
  };

  // Compact mode has no Play Now button, so the whole card advances to the pick.
  const onCardTap = () => {
    playNow("card");
  };

  // Enter or Space activates the compact card, as on a native button.
  const onCardKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onCardTap();
    }
  };

  const cancel = () => {
    // Persist the dismissal across tab-switch remounts and stop the countdown.
    clearCountdown();
    chainPrompt.dismiss(experienceId());
    setState({ dismissed: true, secondsLeft: null });
  };

  // Only the player (or its container) being fullscreen hides this prompt from
  // the viewer. Fullscreening the whole experience container is the mobile
  // immersive layout, which keeps auto-advancing.
  const suppressAutoAdvanceForFullscreen = () => {
    const fsEl =
      document.fullscreenElement ||
      (document as WebkitFullscreenDocument).webkitFullscreenElement;
    if (!fsEl) {
      return false;
    }
    const root = rootRef.current;
    const container =
      root && root.closest ? root.closest(".experience-container") : null;
    return fsEl !== container;
  };

  const tick = () => {
    // A paused video freezes the ring instead of advancing; `ended` overrides
    // the freeze because the natural end also pauses the player.
    const applicationData =
      latest.current.props.experienceState.applicationData;
    const playing = applicationData.getValue(valueNames.VIDEO_PLAYING, true);
    const ended = applicationData.getValue(valueNames.VIDEO_ENDED, false);
    if (!playing && !ended) {
      return;
    }
    setState(s => {
      const current =
        s.secondsLeft == null ? CHAIN_COUNTDOWN_SECONDS : s.secondsLeft;
      if (current - 1 <= 0) {
        return { secondsLeft: 0, zeroCount: s.zeroCount + 1 };
      }
      return { secondsLeft: current - 1 };
    });
  };

  // The countdown reached zero. Runs in the commit that shows 0, like the
  // setState callback it replaces.
  const onZero = () => {
    // Advance once, from the timer and outside any store dispatch. Stand
    // down if a second inline instance (portrait) already did.
    clearCountdown();
    const id = experienceId();
    if (advanced.current || chainPrompt.isDismissed(id)) {
      return;
    }
    // A fullscreen video hides the prompt, so do not auto-advance; the viewer
    // advances with Play Now after leaving fullscreen.
    if (countdownFsSuppress.current || suppressAutoAdvanceForFullscreen()) {
      advanced.current = true;
      setState({ secondsLeft: null });
      return;
    }
    advanced.current = true;
    chainPrompt.dismiss(id);
    playNow("auto");
  };

  const startCountdown = () => {
    clearCountdown();
    countdownFsSuppress.current = suppressAutoAdvanceForFullscreen();
    setState({ secondsLeft: CHAIN_COUNTDOWN_SECONDS });
    countdownTimer.current = setInterval(() => tick(), 1000);
  };

  const stopCountdown = () => {
    clearCountdown();
    if (latest.current.state.secondsLeft != null) {
      setState({ secondsLeft: null });
    }
  };

  // Resets on an experience swap and drives the fixed-length end-of-video
  // countdown. The countdown is a wall-clock timer, not derived from the
  // remaining time: entering the end zone starts a fresh run, and seeking after
  // that neither restarts nor stops it.
  const sync = () => {
    const { props: p, state: s } = latest.current;
    const applicationData = p.experienceState.applicationData;
    const id = experienceIdOf(p);

    // Scope the shared dismissed flag to the current experience.
    chainPrompt.syncExperience(id);

    if (id !== forId.current) {
      forId.current = id;
      advanced.current = false;
      countdownFsSuppress.current = false;
      clearCountdown();
      // Seed `dismissed` from the shared flag so a tab-switch remount keeps a
      // prior Cancel.
      setState({
        dismissed: chainPrompt.isDismissed(id),
        secondsLeft: null,
      });
      return;
    }

    const ended = applicationData.getValue(valueNames.VIDEO_ENDED, false);
    const remaining = applicationData.getValue(
      valueNames.VIDEO_REMAINING,
      null,
    ) as number | null;
    const hasPick = s.status === "ready" && (s.chain || []).length > 0;
    const inCountdownZone =
      ended || (remaining != null && remaining <= CHAIN_COUNTDOWN_SECONDS);
    // Only the inline instance counts down and auto-advances. In portrait two
    // inline instances can be mounted; the first to finish dismisses through
    // chainPrompt so the other stands down.
    const shouldRun =
      p.variant === "inline" &&
      hasPick &&
      isChainRevealed(applicationData) &&
      !s.dismissed &&
      !chainPrompt.isDismissed(id) &&
      !advanced.current &&
      // Pixie's Workspace hides this block; no advance the viewer cannot see.
      !isPixieOpen(applicationData);

    // Compact (immersive landscape) mode is a passive suggestion list: no
    // countdown and no auto-advance.
    if (shouldRun && !p.compact) {
      if (countdownTimer.current == null && inCountdownZone) {
        startCountdown();
      }
    } else if (countdownTimer.current != null) {
      stopCountdown();
    }
  };

  // Fires the impression from the commit, not from render, so an uncommitted
  // render cannot log a phantom impression.
  const maybeEmitShown = () => {
    const { props: p, state: s } = latest.current;
    const pick = visiblePick(p, s);
    if (pick && chainImpressions.markOnce("next-shown", experienceIdOf(p))) {
      analytics.track("Chain Next Shown", {
        pix_idexperience: pick.experience_id,
      });
    }
  };

  // The effects mirror the class lifecycle in its order: update latest, mount
  // and unmount, componentDidMount/DidUpdate, the overlay height, then the
  // countdown-zero handler.
  useLayoutEffect(() => {
    latest.current = { props, state };
  });

  useLayoutEffect(() => {
    const unsubscribe = chainStore.subscribe(() => {
      setState({
        chain: chainStore.getChain(),
        status: chainStore.getStatus(),
      });
    });
    return () => {
      unsubscribe();
      clearCountdown();
    };
  }, []);

  useLayoutEffect(() => {
    // Evaluate the impression against the render that just committed, before
    // sync can dismiss the prompt (auto-advance) in the same tick.
    maybeEmitShown();
    sync();
  });

  useOverlayHeightVar(rootRef, latest);

  useLayoutEffect(() => {
    if (state.zeroCount === 0) {
      return;
    }
    onZero();
  }, [state.zeroCount]);

  const pick = visiblePick(props, state);
  if (!pick) {
    return null;
  }
  const experienceData = props.experienceState.experienceData;

  // The countdown value is the fixed timer, or null when not counting.
  const secondsLeft = state.secondsLeft;
  const counting = secondsLeft !== null;
  const elapsed = counting ? CHAIN_COUNTDOWN_SECONDS - secondsLeft : 0;
  const dashOffset = counting
    ? (RING_CIRCUMFERENCE * elapsed) / CHAIN_COUNTDOWN_SECONDS
    : 0;

  // Counting down, the ring and seconds overlay the thumbnail; before that its
  // play glyph shows.
  const ringOrPlay = counting ? (
    <span className="next-video-block__ring tw:relative tw:z-1 tw:flex tw:h-[44px] tw:w-[44px] tw:items-center tw:justify-center">
      <svg width="44" height="44" aria-hidden="true">
        <circle
          className="next-video-block__ring-track tw:stroke-[#eddede]"
          cx="22"
          cy="22"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="3.5"
        />
        <circle
          className="next-video-block__ring-progress tw:stroke-current tw:text-[#c43e3e]"
          cx="22"
          cy="22"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 22 22)"
        />
      </svg>
      <span className="next-video-block__count tw:absolute tw:font-[700] tw:text-[#fff] tw:text-[15px] tw:[font-variant-numeric:tabular-nums]">
        {secondsLeft}
      </span>
    </span>
  ) : (
    <span className="next-video-block__play tw:ml-[4px] tw:h-0 tw:w-0 tw:[border-bottom:10px_solid_transparent] tw:[border-left:15px_solid_rgba(255,255,255,0.9)] tw:[border-top:10px_solid_transparent]" />
  );

  const thumbUrl = chainThumbUrl(experienceData, pick.image);
  const thumbStyle: CSSProperties | undefined = thumbUrl
    ? { backgroundImage: `url(${thumbUrl})` }
    : undefined;

  return (
    <div
      ref={rootRef}
      className={
        `next-video-block ${BLOCK} next-video-block--` +
        props.variant +
        (props.variant === "overlay" ? ` ${OVERLAY}` : "") +
        (counting ? " next-video-block--counting tw:[.landscape_&]:pb-0" : "") +
        (compact ? " next-video-block--compact" : "")
      }
      onClick={compact ? onCardTap : undefined}
      onKeyDown={compact ? onCardKeyDown : undefined}
      role={compact ? "button" : undefined}
      tabIndex={compact ? 0 : undefined}
    >
      <div className="next-video-block__top tw:flex tw:items-center tw:gap-[12px]">
        <span
          className={
            `next-video-block__thumb ${THUMB}` +
            (counting ? " next-video-block__thumb--counting" : "")
          }
          style={thumbStyle}
          aria-hidden="true"
        >
          {ringOrPlay}
        </span>
        <div className="next-video-block__text tw:min-w-0 tw:flex-1">
          <span className="next-video-block__label content tw:block tw:uppercase">
            Up Next
          </span>
          <span className="next-video-block__title content tw:block tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
            <span className="strong">{cleanChainTitle(pick.title)}</span>
          </span>
          <ChainCardMeta item={pick} />
        </div>
      </div>
      {compact ? null : (
        <div className="next-video-block__actions tw:flex tw:gap-[8px]">
          {counting ? (
            <button
              type="button"
              className={`next-video-block__cancel ${ACTION} tw:text-[#555]`}
              onClick={cancel}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className={`next-video-block__play-now ${ACTION} tw:text-[#fff]`}
            onClick={() => playNow("play-now")}
          >
            Play Now
          </button>
        </div>
      )}
    </div>
  );
}
